import React, { useRef, useState } from "react";
import { Alert, Box, CircularProgress } from "@mui/material";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import { login, fetchUserPermissions } from "../../services/auth";
import { useRBAC } from "../../context/RBACContext";
import ReCAPTCHA from "react-google-recaptcha";
import Navbar from "../Navbar";
import Footer from "../../../interviewAI/layout/Footer";
import signInHero from "../../assets/AuthAssets/SignIn/sign-in-hero.png";
import lightningIcon from "../../assets/AuthAssets/SignIn/feature-lightning.svg";
import usersIcon from "../../assets/AuthAssets/SignIn/feature-users.svg";
import teamsIcon from "../../assets/AuthAssets/SignIn/feature-teams.svg";
import mailIcon from "../../assets/AuthAssets/SignIn/mail.svg";
import lockIcon from "../../assets/AuthAssets/SignIn/lock.svg";
import eyeIcon from "../../assets/AuthAssets/SignIn/eye.svg";
import arrowIcon from "../../assets/AuthAssets/SignIn/arrow-right.svg";
import googleIcon from "../../assets/AuthAssets/SignIn/google.svg";
import microsoftIcon from "../../assets/AuthAssets/SignIn/microsoft.svg";
import ssoIcon from "../../assets/AuthAssets/SignIn/sso.svg";
import underlineIcon from "../../assets/AuthAssets/SignIn/accent-underline.svg";

const Login = () => {
  const navigate = useNavigate();
  const { loadPermissions } = useRBAC();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [validationErrors, setValidationErrors] = useState({});
  const recaptchaRef = useRef(null);
  const [captchaToken, setCaptchaToken] = useState(null);

  const validateForm = () => {
    const errors = {};
    if (!email.trim()) errors.email = "Please enter your email address.";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) errors.email = "Please enter a valid work email.";
    if (!password) errors.password = "Please enter your password.";
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError("");
    if (!validateForm()) return;
    if (!captchaToken) {
      setError("Please verify captcha.");
      return;
    }

    setLoading(true);
    try {
      const result = await login({ email: email.trim(), password, captchaToken });
      if (result?.userId) {
        await new Promise((resolve) => setTimeout(resolve, 100));
        await fetchUserPermissions(result.userId);
        await loadPermissions();
      }
      navigate("/dashboard", { replace: true, state: { refreshPermissions: true } });
    } catch (err) {
      setError(err?.message || "Login failed. Please try again.");
    } finally {
      setLoading(false);
      recaptchaRef.current?.reset();
      setCaptchaToken(null);
    }
  };

  const handleSSOLogin = () => {
    const keycloakUrl = `${process.env.REACT_APP_KEYCLOAK_BASE_URL || "http://localhost:8080"}/realms/${process.env.REACT_APP_KEYCLOAK_REALM || "vlink"}/protocol/openid-connect/auth`;
    const clientId = process.env.REACT_APP_KEYCLOAK_CLIENT_ID || "app-client-talenttalks";
    const redirectUri = process.env.REACT_APP_KEYCLOAK_REDIRECT_URI || `${window.location.origin}/auth/callback`;
    const params = new URLSearchParams({ client_id: clientId, response_type: "code", redirect_uri: redirectUri, scope: "openid email profile" });
    window.location.href = `${keycloakUrl}?${params.toString()}`;
  };

  const updateField = (field) => (event) => {
    const value = event.target.value;
    if (field === "email") setEmail(value);
    if (field === "password") setPassword(value);
    setValidationErrors((previous) => ({ ...previous, [field]: "" }));
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100vh", paddingTop: "80px" }}>
      <Navbar />
      <main className="tt-login" aria-label="Sign in">
        <style>{`
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&family=Caveat:wght@400;700&display=swap');
.tt-login {
  --primary: #6A1B9A;
  --blue: #00AEEF;
  --heading: #0B1F44;
  --body: #334155;
  --muted: #6B7280;
  --border: #E3E7EE;
  --cta: linear-gradient(125deg,#00AEEF 0%,#3A5BC7 45%,#6A1B9A 90%);
  --cta-hover: linear-gradient(125deg,#00AEEF 0%,#3A4AB0 40%,#4A1270 85%);
  --text-gradient: linear-gradient(100deg,#00AEEF 0%,#6A1B9A 85%);
  position: relative;
  isolation: isolate;
  overflow: hidden;
  flex: 1;
  background: #F5F8FF;
  color: var(--body);
  font-family: "Inter", Arial, sans-serif;
  text-align: left;
  -webkit-font-smoothing: antialiased;
  padding: 48px 0 80px;
}
.tt-login *, .tt-login *::before, .tt-login *::after { box-sizing: border-box; }
.tt-login a { color: var(--primary); text-decoration: none; }
.tt-login button, .tt-login input { font: inherit; }
.tt-login button { cursor: pointer; }
.tt-login button:disabled { cursor: wait; opacity: .65; }
.tt-login :focus-visible { outline: 3px solid rgba(0,174,239,.5); outline-offset: 3px; }
.tt-login .background-glow {
  position: absolute;
  inset: 0;
  z-index: -1;
  background: radial-gradient(ellipse 76.8093% 53.3615% at 11.5953% 28.6554%, rgba(0,174,239,.10) 0%, rgba(233,216,255,.28) 45%, rgba(245,248,255,.95) 75%);
}
.tt-login .hero-art {
  position: absolute;
  left: calc(50% - 367px);
  top: -88px;
  width: 1126px;
  height: auto;
  max-width: none;
  pointer-events: none;
  user-select: none;
  z-index: -1;
}
.tt-login .main-content {
  position: relative;
  display: grid;
  grid-template-columns: minmax(0,1fr) minmax(0,536px);
  align-items: start;
  gap: 88px;
  width: min(1290px, calc(100% - 96px));
  margin: 0 auto;
}
.tt-login .intro-panel { position: relative; min-width: 0; padding-top: 17px; }
.tt-login .dot-grid {
  position: absolute;
  left: 363px;
  top: 20px;
  width: 70px;
  height: 70px;
  background-image: radial-gradient(circle at 2px 2px,#BFE7F8 0 2px,transparent 2.15px);
  background-size: 22px 22px;
}
.tt-login .welcome-pill {
  display: inline-flex;
  align-items: center;
  height: 28px;
  padding: 0 10px;
  border-radius: 999px;
  border: 1px solid var(--border);
  background: #F5F8FF;
  color: var(--primary);
  font-size: 16px;
  font-weight: 600;
  letter-spacing: 1.6px;
}
.tt-login .intro-panel h1 {
  width: 424px;
  max-width: 100%;
  margin: 6px 0 0;
  font-family: "Helvetica Neue","Inter",Arial,sans-serif;
  font-size: 50px;
  font-weight: 800;
  letter-spacing: 0;
  line-height: 50px;
  color: var(--heading);
}
.tt-login .intro-panel h1 span { background: var(--text-gradient); background-clip: text; color: transparent; -webkit-background-clip: text; -webkit-text-fill-color: transparent; }
.tt-login .intro-copy { max-width: 448px; margin: 28px 0 0; font-size: 20px; font-weight: 600; line-height: 26px; color: #4B5563; }
.tt-login .feature-list { display: grid; gap: 20px; margin-top: 40px; }
.tt-login .feature { display: flex; align-items: center; gap: 20px; min-height: 60px; }
.tt-login .feature-icon { width: 60px; height: 60px; flex: none; display: grid; place-items: center; border-radius: 12px; background: #E9D8FF; box-shadow: 0 1px 2px #0000000D; }
.tt-login .feature-icon img { width: 30px; height: 30px; }
.tt-login .feature-copy { min-width: 0; }
.tt-login .feature-copy h2 { margin: 0; color: #0F172A; font-size: 18px; font-weight: 700; line-height: 1.25; }
.tt-login .feature-copy p { margin: 5px 0 0; color: var(--muted); font-size: 16px; font-weight: 500; line-height: 1.4; }
.tt-login .handwritten-accent { position: relative; margin: 20px 0 0 10px; width: 135px; color: var(--primary); transform: rotate(23.17deg); transform-origin: top left; }
.tt-login .handwritten-lines { font-family: "Caveat",cursive; font-size: 27.324px; font-weight: 400; line-height: 29.808px; }
.tt-login .handwritten-lines span { display: block; }
.tt-login .handwritten-accent img { display: block; width: 135px; height: 12px; margin-top: 4px; }
.tt-login .signin-card { position: relative; min-width: 0; width: 100%; padding: 54px 40px 40px; border: 1px solid #E8ECF3; border-radius: 24px; background: #FFF; box-shadow: 0 8px 10px -6px rgba(226,232,240,.6),0 20px 25px -5px rgba(106,27,154,.12); }
.tt-login .form-header { text-align: center; margin-bottom: 42px; }
.tt-login .form-header h2 { margin: 0; color: var(--heading); font-family: "Helvetica Neue","Inter",Arial,sans-serif; font-size: 36px; font-weight: 700; letter-spacing: -.75px; line-height: 36px; }
.tt-login .form-header p { margin: 14px 0 0; color: #4B5563; font-size: 18px; font-weight: 500; line-height: 1.4; }
.tt-login .signin-form { display: grid; gap: 28px; }
.tt-login .field { min-width: 0; }
.tt-login .field label { display: block; margin-bottom: 8px; color: var(--body); font-size: 16px; font-weight: 600; line-height: 20px; }
.tt-login .input-shell { position: relative; display: flex; align-items: center; width: 100%; height: 50px; border: 1px solid var(--border); border-radius: 8px; background: #FFF; box-shadow: 0 1px 2px #0000000D; }
.tt-login .password-shell { height: 46px; }
.tt-login .input-shell:focus-within { border-color: var(--blue); box-shadow: 0 0 0 3px rgba(0,174,239,.16); }
.tt-login .input-shell.invalid { border-color: #EF4444; }
.tt-login .field-icon { position: absolute; left: 14px; width: 20px; max-height: 20px; pointer-events: none; }
.tt-login .input-shell input { width: 100%; height: 100%; min-width: 0; padding: 0 16px 0 45px; border: 0; border-radius: 8px; background: transparent; color: var(--body); font-size: 14px; outline: none; }
.tt-login .password-shell input { padding-right: 44px; }
.tt-login .input-shell input::placeholder { color: #94A3B8; opacity: 1; }
.tt-login .password-toggle { position: absolute; right: 3px; top: 0; display: grid; place-items: center; width: 38px; height: 44px; padding: 0; border: 0; background: transparent; }
.tt-login .password-toggle img { width: 20px; height: 20px; }
.tt-login .field-error { margin: 6px 0 0; color: #EF4444; font-size: 12px; line-height: 1.4; }
.tt-login .forgot-row { display: flex; justify-content: flex-end; margin-top: -22px; }
.tt-login .forgot-row a { font-size: 14px; font-weight: 600; line-height: 20px; }
.tt-login .captcha { display: flex; justify-content: center; min-width: 0; }
.tt-login .signin-button { display: flex; align-items: center; justify-content: center; gap: 8px; width: 100%; height: 50px; padding: 0 12px; border: 0; border-radius: 8px; background: var(--cta); color: #FFF; font-size: 16px; font-weight: 700; box-shadow: 0 10px 30px -10px rgba(106,27,154,.6); transition: background .3s ease, box-shadow .3s ease, transform .3s ease; }
.tt-login .signin-button:hover { background: var(--cta-hover); box-shadow: 0 18px 40px -12px rgba(74,18,112,.75); transform: translateY(-1px); }
.tt-login .signin-button img { width: 16px; height: 16px; }
.tt-login .signup-prompt { margin: 18px 0 0; color: var(--muted); text-align: center; font-size: 13px; line-height: 1.5; }
.tt-login .signup-prompt a { font-weight: 600; }
.tt-login .social-divider { display: flex; align-items: center; gap: 12px; margin: 26px 0 20px; color: var(--muted); text-align: center; font-size: 12px; font-weight: 500; }
.tt-login .social-divider::before,.tt-login .social-divider::after { content: ""; flex: 1; height: 1px; background: var(--border); }
.tt-login .social-logins { display: grid; grid-template-columns: repeat(3,minmax(0,1fr)); gap: 10px; }
.tt-login .social-button { display: flex; align-items: center; justify-content: center; gap: 7px; min-width: 0; height: 50px; padding: 0 4px; border: 1.243px solid var(--border); border-radius: 8px; background: #FFF; color: var(--body); font-size: 16px; font-weight: 600; box-shadow: 0 1.243px 2.487px #0000000D; }
.tt-login .social-button:hover { border-color: var(--blue); }
.tt-login .social-button img { flex: none; width: 20px; height: 20px; }
.tt-login .terms-note { margin: 40px 0 0; color: var(--muted); font-size: 14px; line-height: 1.5; text-align: center; }
.tt-login .auth-alert { margin: 0 0 20px; }
@media (max-width: 1200px) {
  .tt-login .main-content { width: calc(100% - 64px); gap: 40px; grid-template-columns: minmax(0,1fr) minmax(0,480px); }
  .tt-login .signin-card { padding-right: 28px; padding-left: 28px; }
  .tt-login .intro-panel h1 { font-size: 46px; line-height: 1; }
  .tt-login .intro-copy { font-size: 18px; }
  .tt-login .dot-grid { left: auto; right: 0; }
}
@media (max-width: 1000px) {
  .tt-login .main-content { grid-template-columns: 1fr; width: min(680px,calc(100% - 48px)); gap: 40px; }
  .tt-login .intro-panel { min-height: 0; }
  .tt-login .hero-art { left: calc(50% - 210px); top: -20px; width: 980px; opacity: .35; }
  .tt-login .signin-card { max-width: 536px; margin: 0 auto; padding: 48px 40px 40px; }
}
@media (max-width: 900px) {
  .tt-login .hero-art { left: 50%; top: 16px; width: 980px; opacity: .2; transform: translateX(-42%); }
}
@media (max-width: 620px) {
  .tt-login .handwritten-accent { display: none; }
}
@media (max-width: 600px) {
  .tt-login { padding: 32px 0 48px; }
  .tt-login .main-content { width: calc(100% - 32px); gap: 32px; }
  .tt-login .intro-panel { min-height: 0; }
  .tt-login .intro-panel h1 { font-size: 40px; line-height: 1.05; width: 360px; }
  .tt-login .intro-copy { margin-top: 24px; font-size: 17px; line-height: 1.5; }
  .tt-login .feature-list { margin-top: 32px; gap: 18px; }
  .tt-login .feature { gap: 14px; }
  .tt-login .feature-copy h2 { font-size: 17px; }
  .tt-login .feature-copy p { font-size: 14px; }
  .tt-login .dot-grid { opacity: .45; }
  .tt-login .handwritten-accent { margin-top: 24px; }
  .tt-login .signin-card { padding: 32px 22px; }
  .tt-login .form-header h2 { font-size: 30px; line-height: 1.2; }
  .tt-login .form-header p { font-size: 15px; }
  .tt-login .form-header { margin-bottom: 30px; }
  .tt-login .social-button { font-size: 12px; gap: 5px; }
  .tt-login .social-button img { width: 17px; height: 17px; }
}
@media (max-width: 380px) {
  .tt-login .captcha { height: 70px; }
  .tt-login .captcha > div { transform: scale(.9); transform-origin: center top; flex-shrink: 0; }
  .tt-login .intro-panel h1 { font-size: 38px; }
  .tt-login .social-logins { grid-template-columns: 1fr; }
  .tt-login .social-button { font-size: 15px; height: 44px; }
}
@media (max-width: 430px) {
  .tt-login .main-content { gap: 26px; }
  .tt-login .intro-panel { padding-top: 0; }
  .tt-login .intro-panel h1 { font-size: clamp(32px, 9vw, 38px); }
  .tt-login .intro-copy { margin-top: 16px; font-size: 15px; }
  .tt-login .feature-list { margin-top: 24px; gap: 12px; }
  .tt-login .feature-icon { width: 48px; height: 48px; }
  .tt-login .feature-icon img { width: 25px; height: 25px; }
  .tt-login .feature { min-height: 48px; }
  .tt-login .signin-card { padding: 28px 18px; border-radius: 18px; }
  .tt-login .signin-form { gap: 22px; }
  .tt-login .forgot-row { margin-top: -16px; }
  .tt-login .terms-note { margin-top: 24px; }
}
`}</style>
        <div className="background-glow" aria-hidden="true" />
        <img className="hero-art" src={signInHero} alt="" aria-hidden="true" />
        <div className="main-content">
          <section className="intro-panel" aria-labelledby="login-page-heading">
            <div className="dot-grid" aria-hidden="true" />
            <div className="welcome-pill">WELCOME BACK</div>
            <h1 id="login-page-heading">Smarter Interviews.<br /><span>Brighter Teams.</span></h1>
            <p className="intro-copy">Sign in to continue building high-performing teams through AI-powered interviews with human-in-the-loop evaluation.</p>
            <div className="feature-list">
              {[
                [lightningIcon, "AI-Powered Interviews", "Save time and hire faster."],
                [usersIcon, "Better Candidates", "Data-driven, unbiased insights."],
                [teamsIcon, "Stronger Teams", "Make confident hiring decisions."],
              ].map(([icon, title, description]) => (
                <article className="feature" key={title}>
                  <div className="feature-icon"><img src={icon} alt="" /></div>
                  <div className="feature-copy"><h2>{title}</h2><p>{description}</p></div>
                </article>
              ))}
            </div>
            <div className="handwritten-accent" aria-label="People Build Possibilities">
              <div className="handwritten-lines" aria-hidden="true">
                <span>People</span><span>Build</span><span>Possibilities</span>
              </div>
              <img src={underlineIcon} alt="" aria-hidden="true" />
            </div>
          </section>
          <section className="signin-card" aria-labelledby="signin-title">
            <div className="form-header">
              <h2 id="signin-title">Welcome Back</h2>
              <p>Sign in to your Viriqo Hires account</p>
            </div>
            {error && <Alert severity="error" className="auth-alert">{error}</Alert>}
            <form className="signin-form" onSubmit={handleSubmit} noValidate>
              <div className="field">
                <label htmlFor="tt-email">Email Address</label>
                <div className={`input-shell ${validationErrors.email ? "invalid" : ""}`}>
                  <img className="field-icon" src={mailIcon} alt="" />
                  <input id="tt-email" type="email" placeholder="you@company.com" autoComplete="email" value={email} onChange={updateField("email")} required disabled={loading} aria-invalid={Boolean(validationErrors.email)} aria-describedby={validationErrors.email ? "login-email-error" : undefined} />
                </div>
                {validationErrors.email && <p className="field-error" id="login-email-error" role="alert">{validationErrors.email}</p>}
              </div>
              <div className="field">
                <label htmlFor="tt-password">Password</label>
                <div className={`input-shell password-shell ${validationErrors.password ? "invalid" : ""}`}>
                  <img className="field-icon" src={lockIcon} alt="" />
                  <input id="tt-password" type={showPassword ? "text" : "password"} placeholder="Enter your password" autoComplete="current-password" value={password} onChange={updateField("password")} required disabled={loading} aria-invalid={Boolean(validationErrors.password)} aria-describedby={validationErrors.password ? "login-password-error" : undefined} />
                  <button className="password-toggle" type="button" aria-label={showPassword ? "Hide password" : "Show password"} aria-pressed={showPassword} onClick={() => setShowPassword((visible) => !visible)} disabled={loading}><img src={eyeIcon} alt="" /></button>
                </div>
                {validationErrors.password && <p className="field-error" id="login-password-error" role="alert">{validationErrors.password}</p>}
              </div>
              <div className="forgot-row"><RouterLink to="/forgot-password">Forgot password?</RouterLink></div>
              <div className="captcha">
                <ReCAPTCHA ref={recaptchaRef} sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY} onChange={(token) => setCaptchaToken(token)} theme="light" />
              </div>
              <button className="signin-button" type="submit" disabled={loading}>
                {loading ? <CircularProgress size={22} sx={{ color: "#fff" }} /> : <><span>Sign In</span><img src={arrowIcon} alt="" /></>}
              </button>
            </form>
            <p className="signup-prompt">Don't have an account? <RouterLink to="/signup">Create your account.</RouterLink></p>
            <div className="social-divider" role="separator" aria-label="Or continue with"><span>Or continue with</span></div>
            <div className="social-logins" aria-label="Social sign in options">
              <button className="social-button" type="button" onClick={() => setError("Google sign-in is not configured yet.")} aria-label="Continue with Google"><img src={googleIcon} alt="" /><span>Google</span></button>
              <button className="social-button" type="button" onClick={() => setError("Microsoft sign-in is not configured yet.")} aria-label="Continue with Microsoft"><img src={microsoftIcon} alt="" /><span>Microsoft</span></button>
              <button className="social-button" type="button" onClick={handleSSOLogin} aria-label="Continue with SSO"><img src={ssoIcon} alt="" /><span>SSO</span></button>
            </div>
            <p className="terms-note">By signing in, you agree to our <RouterLink to="/terms-of-service">Terms of Service</RouterLink> and <RouterLink to="/privacy-policy">Privacy Policy</RouterLink>.</p>
          </section>
        </div>
      </main>
      <Footer />
    </Box>
  );
};

export default Login;
